import {useCallback, useState} from 'react';

import {
  useCancelableEffect,
  useDebounce,
} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CancelToken} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {ContactSourcesEnum} from '../../../../../../libs/shared/core/src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {useRecipientsFetcher} from '../../../../../../srcCommon/inboxes/recipients/recipientsHooks';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {ContactNameHandle} from '../searchModifiersHooks';

type SearchHintsSuggestionsProps = {
  query: string;
  cellsContext?: CellsContext;
  initialSuggestions?: ReadonlyArray<ContactNameHandle>;
  selectedHandles?: ReadonlyArray<ContactNameHandle>;
};

const SEARCH_HINT_CONTACT_SUGGESTIONS_DEBBOUNCE_WAIT_MS = 500;

// @TODO Khaled: see if we can use the useAsyncSearchDropdownQueries hook instead.
export const useSearchHintsContactSearch = ({
  cellsContext,
  query,
  initialSuggestions,
}: SearchHintsSuggestionsProps) => {
  const company = useAppSelector(getCompany);
  const [suggestions, setSuggestions] = useState<ReadonlyArray<ContactNameHandle>>(initialSuggestions ?? []);
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADED);

  const {fetcher} = useRecipientsFetcher({
    company,
    namespace: cellsContext?.namespace ?? '',
    source: ContactSourcesEnum.EMAIL,
    role: RecipientRolesEnum.FROM,
    shouldExcludeGroups: true,
  });

  const refetch = useCallback(
    async (queryString: string, cancelToken: CancelToken) => {
      try {
        const res = await fetcher(queryString, [], cancelToken, undefined);
        const newSuggestions = res.items.filter(
          (item): item is VisualRecipientModel => !!(item as VisualRecipientModel).contact,
        );
        setSuggestions(
          newSuggestions.map((e) => ({
            name: e.name ?? '',
            handle: e.contact.handle,
          })),
        );
        setStatus(AsyncStatusesEnum.LOADED);
      } catch (e) {
        setStatus(AsyncStatusesEnum.FAILED);
      }
    },
    [fetcher, setStatus, setSuggestions],
  );

  const refetchDebounced = useDebounce(refetch, SEARCH_HINT_CONTACT_SUGGESTIONS_DEBBOUNCE_WAIT_MS);

  // Effect to run when query changes.
  useCancelableEffect(
    (cancelToken) => {
      if (!query || query.length < 2) {
        setSuggestions(initialSuggestions ?? []);
        return;
      }
      setStatus(AsyncStatusesEnum.LOADING);
      refetchDebounced(query, cancelToken);
    },
    [query, refetchDebounced],
  );

  return {suggestions, status};
};
